// components/common/AppAlert.js
// Themed replacement for window.alert / window.confirm / window.prompt so all messages match the app's dark theme.
//
// Usage:
//   const { alert, confirm, prompt } = useAlert();
//   await alert('Project name is required');
//   if (await confirm('Discard unsaved changes?')) { ... }
//   const reason = await prompt('Reason for hold (required):');
//   if (reason === null) return; // cancelled
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const AlertContext = createContext(null);

export const AlertProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  const [promptValue, setPromptValue] = useState('');
  const resolverRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (dialog?.kind === 'prompt' && inputRef.current) inputRef.current.focus();
  }, [dialog]);

  const close = (result) => {
    setDialog(null);
    const r = resolverRef.current;
    resolverRef.current = null;
    if (r) r(result);
  };

  const alert = useCallback((message, title = 'Notice') => {
    return new Promise(resolve => {
      resolverRef.current = resolve;
      setDialog({ kind: 'alert', title, message });
    });
  }, []);

  const confirm = useCallback((message, title = 'Confirm') => {
    return new Promise(resolve => {
      resolverRef.current = resolve;
      setDialog({ kind: 'confirm', title, message });
    });
  }, []);

  const prompt = useCallback((message, title = 'Enter Value', initialValue = '') => {
    return new Promise(resolve => {
      resolverRef.current = resolve;
      setPromptValue(initialValue);
      setDialog({ kind: 'prompt', title, message });
    });
  }, []);

  const onOK = () => {
    if (!dialog) return;
    if (dialog.kind === 'prompt') close(promptValue);
    else if (dialog.kind === 'confirm') close(true);
    else close(undefined);
  };

  const onCancel = () => {
    if (!dialog) return;
    if (dialog.kind === 'prompt') close(null);
    else if (dialog.kind === 'confirm') close(false);
    else close(undefined); // alert: cancel = OK
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); onOK(); }
    else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  };

  return (
    <AlertContext.Provider value={{ alert, confirm, prompt }}>
      {children}
      {dialog && createPortal(
        <div
          className="modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget && dialog.kind !== 'alert') onCancel(); }}
          onKeyDown={handleKeyDown}
          tabIndex={-1}
        >
          <div className="modal modal-small app-alert">
            <div className="app-alert-title">{dialog.title}</div>
            <div className="app-alert-message">{dialog.message}</div>
            {dialog.kind === 'prompt' && (
              <input
                ref={inputRef}
                className="input"
                style={{ width: '100%', marginBottom: '16px' }}
                value={promptValue}
                onChange={(e) => setPromptValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onOK(); } }}
              />
            )}
            <div className="btn-row">
              {dialog.kind !== 'alert' && (
                <button className="btn btn-cancel btn-flex" onClick={onCancel}>Cancel</button>
              )}
              <button className="btn btn-create btn-flex" onClick={onOK} autoFocus={dialog.kind === 'alert'}>
                OK
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </AlertContext.Provider>
  );
};

export const useAlert = () => {
  const ctx = useContext(AlertContext);
  if (!ctx) throw new Error('useAlert must be used within AlertProvider');
  return ctx;
};
